import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Eye, Check, X, RotateCcw, BookOpen, Mic, FileText } from 'lucide-react';
import { SETS } from '../data/jeuxReflexionSets';
import { ENIGME_SETS } from '../data/jeuxReflexionEnigmes';
import { SITUATION_SETS } from '../data/jeuxReflexionSituations';

const LEVELS = [...new Set([...Object.keys(SETS), ...Object.keys(ENIGME_SETS), ...Object.keys(SITUATION_SETS)])];

function QuizCard({ set, onBack }) {
  const [qIndex, setQIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [speakingMode, setSpeakingMode] = useState(false);
  const [userInput, setUserInput] = useState('');

  const total = set.questions.length;
  const q = set.questions[qIndex];
  const progress = ((qIndex + (revealed ? 1 : 0)) / total) * 100;

  const handleAnswer = (knew) => {
    if (knew) setScore(prev => prev + 1);
    setRevealed(false);
    setUserInput('');
    if (qIndex + 1 < total) {
      setQIndex(prev => prev + 1);
    } else {
      setFinished(true);
    }
  };

  const restart = () => {
    setQIndex(0);
    setRevealed(false);
    setScore(0);
    setFinished(false);
    setSpeakingMode(false);
    setUserInput('');
  };

  if (finished && !speakingMode) {
    return (
      <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="quiz-end-screen">
        <div className="bg-[#1A2F4A] px-6 py-8 text-center">
          <p className="text-5xl mb-3">🎉</p>
          <p className="text-white text-sm uppercase tracking-wider mb-1">Ton score</p>
          <p className="text-4xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {score} / {total}
          </p>
          <p className="text-white/50 text-xs mt-2">
            {score === total ? 'Parfait !' : score >= 7 ? 'Très bien !' : score >= 5 ? 'Pas mal !' : 'Continue à pratiquer !'}
          </p>
        </div>
        <div className="p-6 space-y-3">
          <button onClick={restart} className="w-full flex items-center justify-center gap-2 bg-[#1A2F4A] text-white py-3 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors" data-testid="replay-btn">
            <RotateCcw className="w-4 h-4" /> Rejouer
          </button>
          <button onClick={() => setSpeakingMode(true)} className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A2F4A] py-3 rounded-xl text-sm font-bold hover:bg-[#C9A961] transition-colors" data-testid="speaking-btn">
            <Mic className="w-4 h-4" /> Parle maintenant
          </button>
          <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-[#E8E4DC] text-[#1A2F4A] py-3 rounded-xl text-sm font-semibold hover:border-[#D4AF37] transition-colors" data-testid="choose-set-btn">
            <BookOpen className="w-4 h-4" /> Choisir un autre set
          </button>
        </div>
      </div>
    );
  }

  if (speakingMode) {
    return (
      <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="speaking-screen">
        <div className="bg-[#1A2F4A] px-6 py-5">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Mini Speaking</p>
          <p className="text-white font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{set.title}</p>
        </div>
        <div className="p-6 space-y-4">
          {set.speaking.map((prompt, i) => (
            <div key={i} className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5">
              <p className="text-[#1A2F4A] text-base font-medium text-center">{prompt}</p>
            </div>
          ))}
          <div className="bg-[#1A2F4A] rounded-xl p-4 text-center">
            <Mic className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-white text-sm">Réponds à voix haute</p>
            <p className="text-white/40 text-xs mt-1">Pas d'enregistrement — pratique libre</p>
          </div>
          <div className="flex gap-2">
            <button onClick={restart} className="flex-1 flex items-center justify-center gap-2 bg-[#1A2F4A] text-white py-3 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors">
              <RotateCcw className="w-4 h-4" /> Rejouer
            </button>
            <button onClick={onBack} className="flex-1 flex items-center justify-center gap-2 border border-[#E8E4DC] text-[#1A2F4A] py-3 rounded-xl text-sm font-semibold hover:border-[#D4AF37] transition-colors">
              Autre set
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="quiz-card">
      {/* Header */}
      <div className="bg-[#1A2F4A] px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">{set.title}</p>
          <p className="text-white/50 text-xs mt-0.5">Question {qIndex + 1} / {total}</p>
        </div>
        <div className="text-right">
          <p className="text-white text-sm font-bold">Score: {score} / {total}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-[#E8E4DC]">
        <div className="h-full bg-[#D4AF37] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>

      {/* Question */}
      <div className="p-6">
        <div className="text-center py-8">
          <p className="text-6xl mb-6">{q.emoji}</p>
          <p className="text-xl text-[#1A2F4A] font-medium leading-relaxed max-w-sm mx-auto" data-testid="clue-text">
            {q.clue}
          </p>
        </div>

        {!revealed ? (
          <div className="space-y-3">
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Écrivez votre réponse ici…"
              className="w-full text-center text-base border border-[#E8E4DC] rounded-xl px-4 py-3 bg-white focus:border-[#D4AF37] focus:outline-none"
              onKeyDown={(e) => { if (e.key === 'Enter') setRevealed(true); }}
              data-testid="user-input"
            />
            <button
              onClick={() => setRevealed(true)}
              className="w-full flex items-center justify-center gap-2 bg-[#FAF7F0] border border-[#E8E4DC] text-[#1A2F4A] py-3.5 rounded-xl text-sm font-bold hover:border-[#D4AF37] hover:bg-white transition-colors"
              data-testid="reveal-btn">
              <Eye className="w-4 h-4" /> Voir la réponse
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {userInput.trim() && (
              <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-4 text-center" data-testid="user-answer-display">
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">Ta réponse</p>
                <p className="text-lg text-[#1A2F4A] font-medium">{userInput}</p>
              </div>
            )}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5 text-center" data-testid="answer-reveal">
              <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Réponse</p>
              <p className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {q.answer}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleAnswer(true)}
                className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 py-3.5 rounded-xl text-sm font-bold hover:bg-emerald-100 transition-colors"
                data-testid="knew-btn">
                <Check className="w-4 h-4" /> Je savais
              </button>
              <button
                onClick={() => handleAnswer(false)}
                className="flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-800 py-3.5 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors"
                data-testid="didnt-know-btn">
                <X className="w-4 h-4" /> Je ne savais pas
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EnigmeQuiz({ set, onBack }) {
  const [qIndex, setQIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [showOral, setShowOral] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [userInput, setUserInput] = useState('');

  const total = set.questions.length;
  const q = set.questions[qIndex];
  const progress = ((qIndex + (revealed ? 1 : 0)) / total) * 100;

  const handleNext = (knew) => {
    if (knew) setScore(prev => prev + 1);
    setRevealed(false);
    setShowOral(false);
    setUserInput('');
    if (qIndex + 1 < total) {
      setQIndex(prev => prev + 1);
    } else {
      setFinished(true);
    }
  };

  const restart = () => {
    setQIndex(0);
    setRevealed(false);
    setShowOral(false);
    setScore(0);
    setFinished(false);
    setUserInput('');
  };

  if (finished) {
    return (
      <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="enigme-end">
        <div className="bg-[#1A2F4A] px-6 py-8 text-center">
          <p className="text-5xl mb-3">🎉</p>
          <p className="text-white text-sm uppercase tracking-wider mb-1">Ton score</p>
          <p className="text-4xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{score} / {total}</p>
          <p className="text-white/50 text-xs mt-2">
            {score === total ? 'Parfait !' : score >= 7 ? 'Très bien !' : score >= 5 ? 'Pas mal !' : 'Continue !'}
          </p>
        </div>
        <div className="p-6 space-y-4">
          {/* Final speaking task */}
          <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider">Activité finale</p>
            </div>
            <p className="text-base text-[#1A2F4A] font-medium text-center mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {set.finalTask}
            </p>
            <div className="space-y-1.5">
              {set.finalExamples.map((ex, i) => (
                <p key={i} className="text-xs text-[#6B4E37] italic text-center">{ex}</p>
              ))}
            </div>
          </div>
          <button onClick={restart} className="w-full flex items-center justify-center gap-2 bg-[#1A2F4A] text-white py-3 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors" data-testid="enigme-replay">
            <RotateCcw className="w-4 h-4" /> Rejouer
          </button>
          <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-[#E8E4DC] text-[#1A2F4A] py-3 rounded-xl text-sm font-semibold hover:border-[#D4AF37] transition-colors" data-testid="enigme-back">
            <BookOpen className="w-4 h-4" /> Choisir un autre set
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="enigme-card">
      {/* Header */}
      <div className="bg-[#1A2F4A] px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Énigme {qIndex + 1} / {total}</p>
        </div>
        <p className="text-white text-sm font-bold">Score: {score} / {total}</p>
      </div>
      <div className="h-1 bg-[#E8E4DC]">
        <div className="h-full bg-[#D4AF37] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>

      <div className="p-6">
        {/* Riddle */}
        <div className="text-center py-6">
          <p className="text-6xl mb-5">{q.emoji}</p>
          <div className="space-y-1">
            {q.clue.split('\n').map((line, i) => (
              <p key={i} className="text-lg text-[#1A2F4A] font-medium">{line}</p>
            ))}
          </div>
        </div>

        {!revealed ? (
          <div className="space-y-3">
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Écris ta réponse ici…"
              className="w-full text-center text-base border border-[#E8E4DC] rounded-xl px-4 py-3 bg-white focus:border-[#D4AF37] focus:outline-none"
              onKeyDown={(e) => { if (e.key === 'Enter') setRevealed(true); }}
              data-testid="enigme-input"
            />
            <button
              onClick={() => setRevealed(true)}
              className="w-full flex items-center justify-center gap-2 bg-[#FAF7F0] border border-[#E8E4DC] text-[#1A2F4A] py-3.5 rounded-xl text-sm font-bold hover:border-[#D4AF37] hover:bg-white transition-colors"
              data-testid="enigme-reveal">
              <Eye className="w-4 h-4" /> Voir la réponse
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {userInput.trim() && (
              <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-4 text-center" data-testid="enigme-user-answer">
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">Ta réponse</p>
                <p className="text-lg text-[#1A2F4A] font-medium">{userInput}</p>
              </div>
            )}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5 text-center" data-testid="enigme-answer">
              <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Réponse</p>
              <p className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{q.answer}</p>
            </div>

            {/* B2 Task — shown after answer if present */}
            {q.task && (
              <div className="bg-[#1A2F4A]/5 border border-[#1A2F4A]/15 rounded-xl p-5" data-testid="enigme-task">
                <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold mb-2">Tâche B2 — Production</p>
                <p className="text-sm text-[#1A2F4A] leading-relaxed">{q.task}</p>
              </div>
            )}

            {/* Oral practice toggle — only if this question has oral prompts */}
            {q.oral && q.oral.length > 0 && (
              !showOral ? (
                <button
                  onClick={() => setShowOral(true)}
                  className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A2F4A] py-3 rounded-xl text-sm font-bold hover:bg-[#C9A961] transition-colors"
                  data-testid="enigme-oral-toggle">
                  <Mic className="w-4 h-4" /> Pratique orale
                </button>
              ) : (
                <div className="bg-[#1A2F4A] rounded-xl p-5 space-y-2" data-testid="enigme-oral-questions">
                  <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-2">Réponds à voix haute</p>
                  {q.oral.map((oq, i) => (
                    <p key={i} className="text-white text-sm">• {oq}</p>
                  ))}
                </div>
              )
            )}

            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => handleNext(true)} className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 py-3.5 rounded-xl text-sm font-bold hover:bg-emerald-100 transition-colors" data-testid="enigme-knew">
                <Check className="w-4 h-4" /> Je savais
              </button>
              <button onClick={() => handleNext(false)} className="flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-800 py-3.5 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors" data-testid="enigme-didnt-know">
                <X className="w-4 h-4" /> Je ne savais pas
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SituationQuiz({ set, onBack }) {
  const [qIndex, setQIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [finished, setFinished] = useState(false);
  const [userInput, setUserInput] = useState('');

  const total = set.questions.length;
  const q = set.questions[qIndex];
  const progress = ((qIndex + (revealed ? 1 : 0)) / total) * 100;

  const handleNext = () => {
    setRevealed(false);
    setUserInput('');
    if (qIndex + 1 < total) {
      setQIndex(prev => prev + 1);
    } else {
      setFinished(true);
    }
  };

  const restart = () => {
    setQIndex(0);
    setRevealed(false);
    setFinished(false);
    setUserInput('');
  };

  if (finished) {
    return (
      <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="situation-end">
        <div className="bg-[#1A2F4A] px-6 py-8 text-center">
          <p className="text-5xl mb-3">🎉</p>
          <p className="text-white text-sm uppercase tracking-wider mb-1">Activité terminée</p>
          <p className="text-2xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {total} / {total} situations
          </p>
          <p className="text-white/50 text-xs mt-2">Bravo ! Tu as réfléchi à toutes les situations.</p>
        </div>
        <div className="p-6 space-y-3">
          <button onClick={restart} className="w-full flex items-center justify-center gap-2 bg-[#1A2F4A] text-white py-3 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors" data-testid="situation-replay">
            <RotateCcw className="w-4 h-4" /> Recommencer
          </button>
          <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-[#E8E4DC] text-[#1A2F4A] py-3 rounded-xl text-sm font-semibold hover:border-[#D4AF37] transition-colors" data-testid="situation-back-end">
            <BookOpen className="w-4 h-4" /> Choisir une autre activité
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden" data-testid="situation-card">
      {/* Header */}
      <div className="bg-[#1A2F4A] px-6 py-4 flex items-center justify-between">
        <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Situation {qIndex + 1} / {total}</p>
      </div>
      <div className="h-1 bg-[#E8E4DC]">
        <div className="h-full bg-[#D4AF37] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>

      <div className="p-6 space-y-5">
        {/* Situation */}
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5" data-testid="situation-text">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-2">Situation</p>
          <p className="text-base text-[#1A2F4A] leading-relaxed">{q.situation}</p>
        </div>

        {/* Consigne */}
        <div className="text-center" data-testid="situation-consigne">
          <p className="text-sm text-[#6B4E37] italic">{q.consigne}</p>
        </div>

        {/* Input */}
        <textarea
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder="Écris ta réponse ici… (2 à 4 phrases)"
          rows={4}
          className="w-full text-sm border border-[#E8E4DC] rounded-xl px-4 py-3 bg-white focus:border-[#D4AF37] focus:outline-none resize-none leading-relaxed"
          data-testid="situation-input"
        />

        {!revealed ? (
          <button
            onClick={() => setRevealed(true)}
            className="w-full flex items-center justify-center gap-2 bg-[#FAF7F0] border border-[#E8E4DC] text-[#1A2F4A] py-3.5 rounded-xl text-sm font-bold hover:border-[#D4AF37] hover:bg-white transition-colors"
            data-testid="situation-reveal">
            <Eye className="w-4 h-4" /> Voir une réponse possible
          </button>
        ) : (
          <div className="space-y-4">
            {userInput.trim() && (
              <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-4" data-testid="situation-user-answer">
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-2">Ta réponse</p>
                <p className="text-sm text-[#1A2F4A] leading-relaxed">{userInput}</p>
              </div>
            )}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5" data-testid="situation-model-answer">
              <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-2">Réponse possible</p>
              <p className="text-sm text-[#1A2F4A] leading-relaxed italic">{q.reponse}</p>
            </div>
            <button
              onClick={handleNext}
              className="w-full flex items-center justify-center gap-2 bg-[#1A2F4A] text-white py-3.5 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors"
              data-testid="situation-next">
              <ArrowRight className="w-4 h-4" /> {qIndex + 1 < total ? 'Situation suivante' : 'Terminer'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function JeuxReflexion() {
  const [activeLevel, setActiveLevel] = useState('A1');
  const [activeSet, setActiveSet] = useState(null);
  const [activeEnigme, setActiveEnigme] = useState(null);
  const [activeSituation, setActiveSituation] = useState(null);

  const sets = SETS[activeLevel] || [];
  const enigmeSets = ENIGME_SETS[activeLevel] || [];
  const situationSets = SITUATION_SETS[activeLevel] || [];

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      {/* Hero */}
      <section className="pt-20 pb-8">
        <div className="max-w-[720px] mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Link to="/speaking-lab" className="text-xs text-[#999] hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3 text-[#999]" />
            <span className="text-xs text-[#999]">Réfléchir & Jouer</span>
            <ChevronRight className="w-3 h-3 text-[#999]" />
            <span className="text-xs text-[#D4AF37] font-semibold">Jeux de réflexion</span>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="jeux-title">
            Jeux de réflexion linguistique
          </h1>
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="h-px w-12 bg-[#D4AF37]/50" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50" />
            <div className="h-px w-12 bg-[#D4AF37]/50" />
          </div>
          <p className="text-sm text-[#6B4E37] leading-relaxed max-w-md mx-auto">
            Devinez le mot à partir d'indices. Testez votre vocabulaire et parlez en français.
          </p>
        </div>
      </section>

      {/* Level filter */}
      <section className="pb-6">
        <div className="max-w-[720px] mx-auto px-8 flex items-center justify-center gap-2">
          {LEVELS.map(lvl => (
            <button
              key={lvl}
              onClick={() => { setActiveLevel(lvl); setActiveSet(null); setActiveEnigme(null); setActiveSituation(null); }}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                activeLevel === lvl
                  ? 'bg-[#1A2F4A] text-white'
                  : 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
              }`}
              data-testid={`level-btn-${lvl}`}>
              {lvl}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-[520px] mx-auto px-8">
          {activeSituation ? (
            /* Situation quiz */
            <div>
              <button
                onClick={() => setActiveSituation(null)}
                className="flex items-center gap-1.5 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-4"
                data-testid="back-from-situation">
                <ArrowRight className="w-3 h-3 rotate-180" /> Tous les sets
              </button>
              <SituationQuiz set={activeSituation} onBack={() => setActiveSituation(null)} />
            </div>
          ) : activeEnigme ? (
            /* Enigme quiz */
            <div>
              <button
                onClick={() => setActiveEnigme(null)}
                className="flex items-center gap-1.5 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-4"
                data-testid="back-from-enigme">
                <ArrowRight className="w-3 h-3 rotate-180" /> Tous les sets
              </button>
              <EnigmeQuiz set={activeEnigme} onBack={() => setActiveEnigme(null)} />
            </div>
          ) : activeSet ? (
            /* Regular quiz */
            <div>
              <button
                onClick={() => setActiveSet(null)}
                className="flex items-center gap-1.5 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-4"
                data-testid="back-to-sets">
                <ArrowRight className="w-3 h-3 rotate-180" /> Tous les sets
              </button>
              <QuizCard set={activeSet} onBack={() => setActiveSet(null)} />
            </div>
          ) : (
            /* Set selection */
            <div className="space-y-3" data-testid="set-list">
              {sets.length === 0 && enigmeSets.length === 0 && situationSets.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-4xl mb-3">🚧</p>
                  <p className="text-sm text-[#999]">Contenu {activeLevel} bientôt disponible</p>
                </div>
              ) : (
                <>
                  {/* Regular vocabulary sets */}
                  {sets.map((set, i) => (
                    <button
                      key={set.id}
                      onClick={() => setActiveSet(set)}
                      className="w-full bg-white border border-[#E8E4DC] rounded-xl p-5 flex items-center gap-4 hover:border-[#D4AF37] hover:shadow-sm transition-all text-left group"
                      data-testid={`set-btn-${set.id}`}>
                      <div className="w-10 h-10 rounded-full bg-[#1A2F4A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {set.title}
                        </p>
                        <p className="text-xs text-[#999] mt-0.5">{set.questions.length} questions</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#E8E4DC] group-hover:text-[#D4AF37] transition-colors" />
                    </button>
                  ))}

                  {/* Enigmes section */}
                  {enigmeSets.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 pt-4 pb-1" data-testid="enigmes-divider">
                        <div className="h-px flex-1 bg-[#D4AF37]/30" />
                        <p className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider whitespace-nowrap">
                          Énigmes + Expression orale
                        </p>
                        <div className="h-px flex-1 bg-[#D4AF37]/30" />
                      </div>
                      {enigmeSets.map((eset, i) => (
                        <button
                          key={eset.id}
                          onClick={() => setActiveEnigme(eset)}
                          className="w-full bg-[#1A2F4A] border border-[#1A2F4A] rounded-xl p-5 flex items-center gap-4 hover:bg-[#243a58] hover:shadow-md transition-all text-left group"
                          data-testid={`enigme-btn-${eset.id}`}>
                          <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-[#1A2F4A] flex items-center justify-center flex-shrink-0">
                            <Mic className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                              {eset.title}
                            </p>
                            <p className="text-xs text-white/50 mt-0.5">{eset.questions.length} questions</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                        </button>
                      ))}
                    </>
                  )}

                  {/* Situation sets section */}
                  {situationSets.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 pt-4 pb-1" data-testid="situations-divider">
                        <div className="h-px flex-1 bg-[#1A2F4A]/20" />
                        <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider whitespace-nowrap">
                          Situations & Prise de décision
                        </p>
                        <div className="h-px flex-1 bg-[#1A2F4A]/20" />
                      </div>
                      {situationSets.map((sset) => (
                        <button
                          key={sset.id}
                          onClick={() => setActiveSituation(sset)}
                          className="w-full bg-white border-2 border-[#D4AF37]/40 rounded-xl p-5 flex items-center gap-4 hover:border-[#D4AF37] hover:shadow-sm transition-all text-left group"
                          data-testid={`situation-btn-${sset.id}`}>
                          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                              {sset.title}
                            </p>
                            <p className="text-xs text-[#999] mt-0.5">{sset.questions.length} situations</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#E8E4DC] group-hover:text-[#D4AF37] transition-colors" />
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Back to lab */}
      <section className="pb-16">
        <div className="max-w-[520px] mx-auto px-8 text-center">
          <Link
            to="/speaking-lab"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2F4A] border border-[#E8E4DC] px-5 py-2.5 rounded-full hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            data-testid="back-to-lab">
            Speaking Lab <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
